import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { apiKeyService } from '../../services/apiKeyService';
import { useAuthStore } from '../../stores/authStore';

interface ApiKeyInfo {
  id: string;
  api_key: string;
  key_name: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  rate_limit_per_hour: number;
  total_requests: number;
}

interface RegenerateLimit {
  remaining: number;
  used: number;
  max_daily: number;
}

const ApiKeySection: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showKeys, setShowKeys] = useState<{[keyId: string]: boolean}>({});
  const [isCreatingKey, setIsCreatingKey] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [regenerateLimit, setRegenerateLimit] = useState<RegenerateLimit | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  const { isAuthenticated, isInitialized } = useAuthStore();

  const fetchApiKeys = async (retryCount = 0) => {
    if (!isAuthenticated || !isInitialized) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const result = await apiKeyService.getApiKeys();
      setApiKeys(result.keys);
      setRegenerateLimit(result.regenerateLimit);
    } catch (error: any) {
      console.error('Failed to fetch API keys:', error);
      
      // If auth error and first retry, wait a bit more and retry
      if (error.message?.includes('not authenticated') && retryCount < 2) {
        // Retry API key fetch after delay
        setTimeout(() => {
          fetchApiKeys(retryCount + 1);
        }, (retryCount + 1) * 500);
        return;
      }
      
      setError(`Failed to load API keys: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewKey = async () => {
    setIsCreatingKey(true);
    setError(null);

    try {
      const result = await apiKeyService.createApiKey('My API Key', true);
      
      if (result.success) {
        // Refresh the list to show new key
        await fetchApiKeys();
      } else {
        setError(result.error || 'Failed to create API key. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating API key:', error);
      setError('Failed to create API key. Please try again.');
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleRegenerateKey = async () => {
    setIsCreatingKey(true);
    setError(null);

    try {
      const result = await apiKeyService.regenerateApiKey();
      
      if (result.success) {
        // Refresh the list to show updated key
        await fetchApiKeys();
      } else {
        setError(result.error || 'Failed to regenerate API key. Please try again.');
      }
    } catch (error: any) {
      console.error('Error regenerating API key:', error);
      setError('Failed to regenerate API key. Please try again.');
    } finally {
      setIsCreatingKey(false);
    }
  };

  // Removed handleDeactivateKey - users should always have active API key
  // If compromised, just regenerate instead of deactivate

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleShowKey = (keyId: string) => {
    setShowKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      
      // Show feedback
      setCopiedKeyId(keyId);
      
      // Reset feedback after 2 seconds
      setTimeout(() => {
        setCopiedKeyId(null);
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy API key:', err);
      
      // Fallback: select text for manual copy
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Still show feedback for fallback
      setCopiedKeyId(keyId);
      setTimeout(() => {
        setCopiedKeyId(null);
      }, 2000);
    }
  };

  useEffect(() => {
    // Wait a bit for AuthCore to be fully initialized after isInitialized becomes true
    if (isAuthenticated && isInitialized) {
      // Add small delay to ensure AuthCore token is ready
      const timer = setTimeout(() => {
        fetchApiKeys();
      }, 100); // 100ms should be enough for AuthCore to have token ready
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isInitialized]);

  return (
    <Card className="bg-white border-gray-200 shadow-sm">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-xl font-semibold text-gray-900">My API Key</CardTitle>
          </div>
          {apiKeys.length === 0 && (
            <Button 
              onClick={handleCreateNewKey}
              disabled={isCreatingKey}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              {isCreatingKey ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                'Generate Key'
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* API Keys List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading API keys...
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((key) => (
              <div key={key.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                {/* API Key Display */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">API Key</span>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => toggleShowKey(key.id)}
                        className="inline-flex items-center px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-md transition-colors"
                      >
                        {showKeys[key.id] ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={() => copyToClipboard(key.api_key, key.id)}
                        className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                          copiedKeyId === key.id 
                            ? 'bg-green-100 text-green-800 transform scale-105' 
                            : 'bg-blue-100 hover:bg-blue-200 text-blue-800 hover:scale-105'
                        }`}
                      >
                        {copiedKeyId === key.id ? (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"></path>
                              <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"></path>
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="font-mono text-sm text-gray-800 bg-white p-3 rounded border break-all select-all">
                    {showKeys[key.id] ? key.api_key : key.api_key.replace(/(?<=aimetapic_live_).{24}/, '************************')}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm mb-4">
                  <div>
                    <span className="text-gray-500">Rate Limit:</span>
                    <span className="ml-1 font-medium">{key.rate_limit_per_hour}/hour</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Requests:</span>
                    <span className="ml-1 font-medium">{key.total_requests || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Used:</span>
                    <span className="ml-1 font-medium">
                      {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3">
                  <div className="flex gap-2 items-center flex-wrap">
                    <Button
                      onClick={handleRegenerateKey}
                      disabled={isCreatingKey || regenerateLimit?.remaining === 0}
                      className={`${regenerateLimit?.remaining === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                      size="sm"
                    >
                      {isCreatingKey ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                    {/* Removed Deactivate button - users should always have active API key */}
                    {/* If key is compromised, just regenerate instead */}
                  </div>
                  
                  {/* Rate limit info */}
                  {regenerateLimit && (
                    <div className={`text-xs px-3 py-2 rounded-lg ${
                      regenerateLimit.remaining === 0 
                        ? 'bg-red-50 text-red-700 border border-red-200' 
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {regenerateLimit.remaining === 0 ? (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span><strong>Daily limit reached</strong> - Resets at midnight</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span><strong>{regenerateLimit.remaining} regenerations left</strong> today ({regenerateLimit.used}/10 used)</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* API Key Usage Info */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">How to use your API key</h4>
          <div className="bg-gray-50 rounded-md p-4">
            <p className="text-sm text-gray-600 mb-2">Include your API key in requests:</p>
            <code className="block bg-gray-800 text-green-400 p-3 rounded text-sm overflow-x-auto">
              curl -H "X-API-Key: your_api_key_here" \<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;https://api.metapic.app/metadata/generate
            </code>
          </div>
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800 font-medium mb-1">🔒 Security Tips:</p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Never share your API key publicly or in client-side code</li>
              <li>• If you suspect your key is compromised, regenerate it immediately</li>
              <li>• You're limited to {regenerateLimit?.max_daily || 1111111} regenerations per day</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApiKeySection;
